import React, { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Empty, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useDashboardNavigation } from '../../contexts/DashboardNavigationContext';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { ProductDashboardSummarySection } from './ProductDashboardSummarySection';
import { ProductDashboardDetailSection } from './ProductDashboardDetailSection';
import { useProductDashboardData } from './useProductDashboardData';
import styles from './ProductDashboard.module.css';

type ProductSubdomain = 'operations' | 'development' | 'projections' | 'marketfit';

export const ProductDashboard: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const {
    currentLocation,
    navigateTo,
    goBack,
    drillDownToOperations,
  } = useDashboardNavigation();

  const { summary, summaryLoading } = useProductDashboardData({
    productId,
    currentLocationProductName: currentLocation.productName,
    currentLocationProductId: currentLocation.productId,
    navigateTo,
  });

  const [selectedSubdomain, setSelectedSubdomain] = useState<ProductSubdomain>('operations');

  const localBridgeEnabled = isLocalBridgeApiEnabled();
  const productName = summary?.productName ?? currentLocation.productName ?? 'Product';

  const handleOperationsClick = useCallback(() => setSelectedSubdomain('operations'), []);
  const handleDevelopmentClick = useCallback(() => setSelectedSubdomain('development'), []);
  const handleProjectionsClick = useCallback(() => setSelectedSubdomain('projections'), []);
  const handleMarketFitClick = useCallback(() => setSelectedSubdomain('marketfit'), []);

  const handleDrillDownToOperations = useCallback(() => {
    if (productId) drillDownToOperations(productId, productName);
  }, [productId, productName, drillDownToOperations]);

  const handleDealSelect = useCallback(
    (dealId: string, _dealName?: string) => {
      if (productId) {
        navigate(`/dashboard/product/${productId}/marketfit/analysis/${dealId}`);
      }
    },
    [navigate, productId]
  );

  const handleSituationSelect = useCallback(
    (situationId: string) => {
      if (productId && localBridgeEnabled) {
        navigate(`/dashboard/product/${productId}/marketfit/situation/${situationId}`);
      }
    },
    [productId, localBridgeEnabled, navigate]
  );

  const handleSolutionSelect = useCallback(
    (solutionId: string) => {
      if (productId && localBridgeEnabled) {
        navigate(`/dashboard/product/${productId}/marketfit/solution/${solutionId}`);
      }
    },
    [productId, localBridgeEnabled, navigate]
  );

  if (!productId) {
    return (
      <div className={styles.emptyContainer}>
        <Empty description="No product selected" image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={goBack}>
            Go Back
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <ProductDashboardSummarySection
        productName={productName}
        summaryLoading={summaryLoading}
        summary={summary}
        selectedSubdomain={selectedSubdomain}
        onOperations={handleOperationsClick}
        onDevelopment={handleDevelopmentClick}
        onProjections={handleProjectionsClick}
        onMarketFit={handleMarketFitClick}
      />

      <ProductDashboardDetailSection
        productId={productId}
        selectedSubdomain={selectedSubdomain}
        localBridgeEnabled={localBridgeEnabled}
        onDrillDownToOperations={handleDrillDownToOperations}
        onDealSelect={handleDealSelect}
        onSituationSelect={handleSituationSelect}
        onSolutionSelect={handleSolutionSelect}
      />
    </div>
  );
};

export default ProductDashboard;
